/**
 * `useResizablePanel` — drag-to-resize hook for side panels.
 *
 * Behaviour (Notion-style):
 *   - The handle sits on the *outer* edge of the panel (left edge for
 *     a right-side panel). Mouse-down arms the drag; subsequent move
 *     events resize the panel in real time; mouse-up commits.
 *   - Width is persisted to localStorage so the panel reopens at the
 *     same size next session (and across page navigations).
 *   - Min / max clamps prevent the user from making the panel useless
 *     (too narrow to read) or hogging the whole viewport.
 *
 * The hook owns no DOM — it returns `{ width, onMouseDown, isDragging }`
 * for the consumer to wire into its own aside element + handle div.
 */
import * as React from 'react';

interface UseResizablePanelOptions {
  /** Storage key — different panels use different keys so they don't collide. */
  storageKey: string;
  /** Initial width when nothing is in storage. */
  defaultWidth: number;
  /** Lower clamp — panel can't be narrower than this. */
  minWidth: number;
  /** Upper clamp — panel can't be wider than this. */
  maxWidth: number;
  /**
   * Which side of the viewport the panel docks to. Determines the sign
   * of the delta — a right-docked panel grows when the mouse moves
   * LEFT, a left-docked one grows when the mouse moves RIGHT.
   */
  side: 'right' | 'left';
}

interface UseResizablePanelResult {
  width: number;
  isDragging: boolean;
  onHandleMouseDown: (event: React.MouseEvent<HTMLElement>) => void;
  setWidth: (next: number) => void;
}

function readStored(storageKey: string, fallback: number): number {
  try {
    if (typeof localStorage === 'undefined') return fallback;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

function writeStored(storageKey: string, value: number): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* storage may throw in privacy modes */
  }
}

export function useResizablePanel({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  side,
}: UseResizablePanelOptions): UseResizablePanelResult {
  const clamp = React.useCallback(
    (n: number): number => Math.min(maxWidth, Math.max(minWidth, n)),
    [minWidth, maxWidth],
  );

  const [width, setWidthState] = React.useState<number>(() =>
    clamp(readStored(storageKey, defaultWidth)),
  );
  const [isDragging, setIsDragging] = React.useState(false);

  const setWidth = React.useCallback(
    (next: number) => {
      const c = clamp(next);
      setWidthState(c);
      writeStored(storageKey, c);
    },
    [storageKey, clamp],
  );

  // Track-while-dragging refs avoid stale closures inside the document
  // listeners and let us release them cleanly on mouseup.
  const dragStateRef = React.useRef<{ startX: number; startWidth: number } | null>(null);

  const onHandleMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      dragStateRef.current = { startX: event.clientX, startWidth: width };
      setIsDragging(true);
    },
    [width],
  );

  React.useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: MouseEvent): void => {
      const state = dragStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      // Right-docked: dragging left (negative delta) widens the panel.
      // Left-docked: dragging right (positive delta) widens the panel.
      const next = side === 'right' ? state.startWidth - delta : state.startWidth + delta;
      setWidthState(clamp(next));
    };

    const onUp = (): void => {
      dragStateRef.current = null;
      setIsDragging(false);
      // Persist whatever the user landed on.
      setWidthState((current) => {
        writeStored(storageKey, current);
        return current;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // While dragging, every text element on the page can highlight by
    // accident — disable selection at the body level.
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [isDragging, side, clamp, storageKey]);

  return { width, isDragging, onHandleMouseDown, setWidth };
}
